import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { AiOutlineSortAscending } from "react-icons/ai";
import { Eye, Search, AlertCircle } from "lucide-react";
import Loading from "../../component/Loading/Loading";
import AlertMsg from '../../component/AlertMsg/AlertMsg';
import Pagination from "../../utils/Pagenation/Pagenation";
import useAxiosInstance from "../../services/useAxiosInstance";
import { setCommunityId } from "../../redux/authSlice";
import GlassmorphicModal from "../../component/ReusableComponents/GlassmorphicModal";

const CommunityList = () => {
  const axiosInstance = useAxiosInstance();
  const [community, setCommunity] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const dispatch = useDispatch();
  const [selectedPlanTypeId, setSelectedPlanTypeId] = useState(0);
  const [selectedPlanPeriodId, setSelectedPlanPeriodId] = useState(0);
  const [selectedPlanRangeId, setSelectedPlanRangeId] = useState(0);
  const [loading, setLoading] = useState(false);
  const communityId = useSelector((state) => state.auth.communityId);

  const [planPeriodOptions, setPlanPeriodOptions] = useState([]);
  const [planRangeOptions, setPlanRangeOptions] = useState([]);
  const [planTypeOptions, setPlanTypeOptions] = useState([]);
  const [activeStatusOptions, setActiveStatusOptions] = useState([]);
  const [communityActiveStatus, setCommunityActiveStatus] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [currentStatus, setCurrentStatus] = useState(null);

  const [communityUpdateActiveStatus, setCommunityUpdateActiveStatus] = useState(null);
  const [communityIds, setCommunityIds] = useState(null);
  const [sortOrder, setSortOrder] = useState('asc');
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 10;
  const [alert, setAlert] = useState({ visible: false, message: '', type: '' });

  useEffect(() => {
    const fetchActiveStatusOptions = async () => {
      try {
        const response = await axiosInstance.get('/api/public/community/communityByPlans');
        setActiveStatusOptions(response.data);
      } catch (error) {
        console.error('Error fetching active status options:', error);
      }
    };

    fetchActiveStatusOptions();
  }, []);

  useEffect(() => {
    fetchCommunity();
  }, [communityActiveStatus]);

  useEffect(() => {
    fetchSelectOptions();
  }, [selectedPlanTypeId, selectedPlanPeriodId, selectedPlanRangeId]);

  const [filterData, setFilterData] = useState({
    selectedPlanTypeIds: 0,
    selectedPlanPeriodIds: 0,
    selectedPlanRangeIds: 0,
    communityActiveStatuss: null
  });

  const handlePlanTypeChange = (event) => {
    const value = event.target.value === "" ? 0 : parseInt(event.target.value);
    setFilterData({
      ...filterData,
      selectedPlanTypeIds: value
    });
  };

  const handlePlanPeriodChange = (event) => {
    const value = event.target.value === "" ? 0 : parseInt(event.target.value);
    setFilterData({
      ...filterData,
      selectedPlanPeriodIds: value
    });
  };

  const handlePlanRangeChange = (event) => {
    const value = event.target.value === "" ? 0 : parseInt(event.target.value);
    setFilterData({
      ...filterData,
      selectedPlanRangeIds: value
    });
  };

  const handleChangeActiveStatus = (event) => {
    const { value } = event.target;
    setFilterData((filterData) => ({
      ...filterData,
      communityActiveStatuss: value === "true" ? true : value === "false" ? false : null,
    }));
  };

  const handleSearch = () => {
    setSelectedPlanTypeId(filterData.selectedPlanTypeIds);
    setSelectedPlanPeriodId(filterData.selectedPlanPeriodIds);
    setSelectedPlanRangeId(filterData.selectedPlanRangeIds);
    setCommunityActiveStatus(filterData.communityActiveStatuss);
    setCurrentPage(0);
  };

  const fetchCommunity = async () => {
    try {
      setLoading(true);
      console.log("Fetching with params:", {
        planTypeId: selectedPlanTypeId,
        planPeriodId: selectedPlanPeriodId,
        planRangeId: selectedPlanRangeId,
        communityActiveStatus: communityActiveStatus
      });

      const response = await axiosInstance.get(`/api/public/community/communityByPlans`, {
        params: {
          planTypeId: selectedPlanTypeId || 0,
          planPeriodId: selectedPlanPeriodId || 0,
          planRangeId: selectedPlanRangeId || 0,
          communityActiveStatus: communityActiveStatus
        },
      });
      setCommunity(response.data);
    } catch (error) {
      console.error("Error fetching communities:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSelectOptions();
  }, []);

  useEffect(() => {
    fetchCommunity();
  }, [selectedPlanRangeId, selectedPlanPeriodId, selectedPlanTypeId, communityActiveStatus]);

  const fetchSelectOptions = async () => {
    try {
      const periodResponse = await axiosInstance.get(`/api/public/plan-period/all`);
      setPlanPeriodOptions(periodResponse.data);

      const rangeResponse = await axiosInstance.get(`/api/public/plan-range/all`);
      setPlanRangeOptions(rangeResponse.data);

      const typeResponse = await axiosInstance.get(`/api/public/plan-type/all`);
      setPlanTypeOptions(typeResponse.data);
    } catch (error) {
      console.error("Error fetching select options:", error);
    }
  };

  const handleSort = () => {
    const sortedRolePages = [...community].sort((a, b) => {
      const pageNameA = (a.communityName || "").toLowerCase();
      const pageNameB = (b.communityName || "").toLowerCase();

      if (sortOrder === 'asc') {
        return pageNameA.localeCompare(pageNameB);
      } else {
        return pageNameB.localeCompare(pageNameA);
      }
    });

    setCommunity(sortedRolePages);
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
  };

  const handleEdit = (communityId) => {
    setLoading(true);
    dispatch(setCommunityId(communityId));
    setLoading(false);
  };

  const filteredCommunity = community.filter((com) =>
    com.communityName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    com.zipCode?.toString().includes(searchQuery.toLowerCase())
  );

  const pageCount = Math.ceil(filteredCommunity.length / itemsPerPage);
  const filteredData = filteredCommunity.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage
  );

  const clearFilters = () => {
    setFilterData({
      selectedPlanTypeIds: 0,
      selectedPlanPeriodIds: 0,
      selectedPlanRangeIds: 0,
      communityActiveStatuss: null,
    });
    setSelectedPlanTypeId(0);
    setSelectedPlanPeriodId(0);
    setSelectedPlanRangeId(0);
    setCommunityActiveStatus(null);
    setCurrentPage(0);
  };

  const openApprovalModal = (communityIds, communityUpdateActiveStatus) => {
    setLoading(true);
    setCommunityIds(communityIds);
    setCommunityUpdateActiveStatus(communityUpdateActiveStatus);
    setShowApprovalModal(true);
    setLoading(false);
  };

  const handleUpdateStatus = async (status) => {
    setShowApprovalModal(false);

    try {
      setLoading(true);
      const response = await axiosInstance.put(
        `/api/public/community/updateActiveStatus/${communityIds}/activeStatus`,null,
        {
          params: { status },
        }
      );

      if (response.status === 200) {
        setAlert({ visible: true, message: 'Community status updated successfully!', type: 'success' });
        fetchCommunity();
        setCurrentStatus(status);
      } else {
        setAlert({ visible: true, message: `Error updating status: ${response.data}`, type: 'error' });
      }
    } catch (error) {
      console.error('An error occurred while updating community status', error);
      setAlert({ visible: true, message: 'An error occurred while updating community status: Please try again.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    setShowApprovalModal(false);
  };

  // Modal footer with action buttons
  const approvalModalFooter = (
    <div className="flex justify-end space-x-2">
      <button
        className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
        onClick={closeModal}
      >
        Cancel
      </button>
      {communityUpdateActiveStatus ? (
        <button
          className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
          onClick={() => handleUpdateStatus(false)}
        >
          Disapprove
        </button>
      ) : (
        <button
          className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
          onClick={() => handleUpdateStatus(true)}
        >
          Approve
        </button>
      )}
    </div>
  );

  return (
    <div className="bg-gray-50 min-h-screen md:p-4">
      {loading && <Loading />}
      {!loading && (
        <div className="max-w-7xl mx-auto">
          {alert.visible && (
            <AlertMsg
              message={alert.message}
              type={alert.type}
              onClose={() => setAlert({ ...alert, visible: false })}
            />
          )}
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-gray-800">Community List</h1>
          </div>
          <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-6">

            <div className="p-4 flex flex-col lg:flex-row items-start gap-2 md:gap-4">
              <div className="relative w-full lg:w-80 md:mb-4 lg:mb-0">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Search Community Name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2 w-full">
                <select
                  value={filterData.selectedPlanRangeIds || ""}
                  onChange={handlePlanRangeChange}
                  className="px-3 py-[11px] rounded-md border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Plan Range</option>
                  {planRangeOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.planRange}
                    </option>
                  ))}
                </select>

                <select
                  value={filterData.selectedPlanPeriodIds || ""}
                  onChange={handlePlanPeriodChange}
                  className="px-3 py-[11px] rounded-md border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Plan Period</option>
                  {planPeriodOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.planPeriod}
                    </option>
                  ))}
                </select>

                <select
                  value={filterData.selectedPlanTypeIds || ""}
                  onChange={handlePlanTypeChange}
                  className="px-3 py-[11px] rounded-md border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Plan Type</option>
                  {planTypeOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.planType}
                    </option>
                  ))}
                </select>

                <select
                  value={filterData.communityActiveStatuss === null ? "null" : filterData.communityActiveStatuss}
                  onChange={handleChangeActiveStatus}
                  className="px-3 py-[11px] rounded-md border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="null">Active Status</option>
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>

                <div className="flex gap-2 sm:col-span-2 lg:col-span-1">
                  <button
                    onClick={handleSearch}
                    className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                    type="button"
                  >
                    Search
                  </button>

                  {(filterData.selectedPlanRangeIds !== 0 ||
                    filterData.selectedPlanPeriodIds !== 0 ||
                    filterData.selectedPlanTypeIds !== 0 ||
                    filterData.communityActiveStatuss !== null) && (
                      <button
                        onClick={clearFilters}
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                        type="button"
                      >
                        Clear
                      </button>
                    )}
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-blue-400">
                  <tr>
                    <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider flex items-start whitespace-nowrap">
                      Community Name
                      <button className="" onClick={handleSort} aria-label="Sort">
                        <AiOutlineSortAscending className="h-5 w-5" />
                      </button>
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider whitespace-nowrap">Community Code</th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider whitespace-nowrap">Plan Expire Date</th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider whitespace-nowrap">Community Size</th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider whitespace-nowrap">Status</th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider whitespace-nowrap">Action</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredData.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-4 text-center text-sm text-gray-500">No Data Found</td>
                    </tr>
                  ) : (
                    filteredData.map((community, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{community.communityName}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{community.communityCode}</td>
                        <td
                          className={`px-6 py-4 whitespace-nowrap text-sm ${new Date(community.planExpireDate) - new Date() <= 10 * 24 * 60 * 60 * 1000 &&
                            new Date(community.planExpireDate) - new Date() > 0
                            ? 'text-yellow-600'
                            : new Date(community.planExpireDate) - new Date() <= 0
                              ? 'text-red-600'
                              : 'text-gray-900'
                            }`}
                        >
                          {new Date(community.planExpireDate).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{community.communitySize}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${community.communityActiveStatus
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                            }`}>
                            {community.communityActiveStatus ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex gap-3">
                            <Link
                              onClick={() => handleEdit(community.communityId)}
                              to="/community-details"
                              className="text-blue-500 hover:text-blue-700"
                              title="View Community"
                            >
                              <Eye size={18} />
                            </Link>
                            <button
                              onClick={() => openApprovalModal(community.communityId, community.communityActiveStatus)}
                              className="text-gray-500 hover:text-gray-700"
                              title="Change Status"
                            >
                              <AlertCircle size={18} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="bg-gray-50 px-6 py-3 flex items-center justify-between border-t border-gray-200">
              <div className="text-sm text-gray-700">
                Page {currentPage + 1} of {pageCount}
              </div>
              <div className="flex-1 flex justify-end">
                <Pagination
                  currentPage={currentPage}
                  pageCount={pageCount}
                  setCurrentPage={setCurrentPage}
                />
              </div>
            </div>
          </div>

          {/* Approval Modal using GlassmorphicModal */}
          <GlassmorphicModal
            isOpen={showApprovalModal}
            onClose={closeModal}
            title="Update Community Status"
            size="md"
            footer={approvalModalFooter}
          >
            <p className="text-gray-700 mb-4">
              Are you sure you want to {communityUpdateActiveStatus ? "deactivate" : "activate"} this community? This will change its current status.
            </p>
          </GlassmorphicModal>
        </div>
      )}
    </div>
  );
};

export default CommunityList;